/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IProductsDimensionsAvailabilities } from '@msdyn365-commerce/commerce-entities';
import { IPriceComponentResources, ISwatchItem, PriceComponent } from '@msdyn365-commerce/components';
import {
    IAny,
    IComponent,
    IComponentProps,
    ICoreContext,
    IGeneric,
    IGridSettings,
    IImageData,
    IImageSettings,
    Image
} from '@msdyn365-commerce/core';
import {
    AttributeSwatch,
    ProductBehavior,
    ProductDimension,
    ProductInventoryAvailability,
    ProductPrice,
    ProductSearchResult
} from '@msdyn365-commerce/retail-proxy';
import {
    ArrayExtensions,
    convertDimensionTypeToProductDimensionType,
    Dictionary,
    DimensionTypes,
    generateImageUrl,
    getProductPageUrlSync,
    IDimensionsApp
} from '@msdyn365-commerce-modules/retail-actions';
import {
    format,
    getPayloadObject,
    getTelemetryAttributes,
    ITelemetryContent,
    onTelemetryClick
} from '@msdyn365-commerce-modules/utilities';
import React, { useState } from 'react';
import { IUnitOfMeasureConversionResponseMapEntity } from '../../../actions/DataServiceEntities.g';
import { CommercePropertyValueEnum, GetExtensionPropertyValueByKey } from '../../../utils';

export interface IMilProductComponentProps extends IComponentProps<{ product?: ProductSearchResult }> {
    className?: string;
    imageSettings?: IImageSettings;
    savingsText?: string;
    freePriceText?: string;
    originalPriceText?: string;
    currentPriceText?: string;
    shouldDisplayPrice?: boolean;
    ratingAriaLabel?: string;
    ratingCountAriaLabel?: string;
    allowBack?: boolean;
    telemetryContent?: ITelemetryContent;
    quickViewButton?: React.ReactNode;
    productComparisonButton?: React.ReactNode;
    isPriceMinMaxEnabled?: boolean;
    priceResources?: IPriceComponentResources;
    inventoryLabel?: string;
    dimensionAvailabilities?: IProductsDimensionsAvailabilities[];
    swatchItemAriaLabel?: string;
    desiredNonstockCommerceName?: string;
    nonstockCommerceState?: string;
    stockStatusText?: string;
    surchargeLabel?: string;
    productUom?: IUnitOfMeasureConversionResponseMapEntity | undefined;
    showListView: boolean;
    minimumOrderQtyLabelText: string;
    availableStockLabel: string;
    productBehavior: ProductBehavior | undefined;
    availInvs: ProductInventoryAvailability | undefined;
}

export interface IProductComponent extends IComponent<IMilProductComponentProps> {}

/**
 * Gets the product page url from the default swatch selected.
 * @param  productData - Product card to be rendered.
 * @returns The default color swatch selected if any.
 */
export function getDefaultSwatchSelected(coreContext: ICoreContext, productData?: ProductSearchResult): AttributeSwatch | null {
    if (!productData || !productData.AttributeValues) {
        return null;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const attributeSwatches = productData.AttributeValues.find(
        attributeValue => attributeValue.KeyName?.toLocaleLowerCase() === dimensionToPreSelectInProductCard
    )?.Swatches;

    if (!ArrayExtensions.hasElements(attributeSwatches)) {
        return null;
    }

    const defaultSwatch = attributeSwatches.find(item => item.IsDefault === true) ?? attributeSwatches[0];
    return defaultSwatch;
}

/**
 * Gets the product image from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
export function getProductImageUrlFromDefaultSwatch(coreContext: ICoreContext, productData?: ProductSearchResult): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    const swatchProductImageUrls = defaultSwatch?.ProductImageUrls;

    if (!ArrayExtensions.hasElements(swatchProductImageUrls)) {
        return productData?.PrimaryImageUrl;
    }

    return generateImageUrl(swatchProductImageUrls[0], coreContext.request.apiSettings);
}

/**
 * Updates the product url link to product details page.
 * @param  productDetailsPageUrl - Product page url.
 * @param  coreContext - Context of the module using the component.
 * @param  queryString - Querystring to be added to the URL.
 * @returns The update product page url.
 */
function updateProductUrl(productDetailsPageUrl: string, coreContext: ICoreContext, queryString: string): string {
    const sourceUrl = new URL(productDetailsPageUrl, coreContext.request.apiSettings.baseUrl);
    if (sourceUrl.search) {
        sourceUrl.search += `&${queryString}`;
    } else {
        sourceUrl.search += queryString;
    }

    const updatedUrl = new URL(sourceUrl.href);
    return updatedUrl.pathname + sourceUrl.search;
}

/**
 * Gets the react node for product unit of measure display.
 * @param  unitOfMeasure - DefaultUnitOfMeasure property from product.
 * @returns The node representing markup for unit of measure component.
 */
// function renderProductUnitOfMeasure(unitOfMeasure?: string): JSX.Element | null {
//     if (!unitOfMeasure) {
//         return null;
//     }
//     return (
//         <div className='msc-product__unit-of-measure'>
//             <span>{unitOfMeasure}</span>
//         </div>
//     );
// }

// /**
//  * Gets the react node for product availability.
//  * @param inventoryAvailabilityLabel - The product information.
//  * @returns The node representing markup for product availability.
//  */
// function renderProductAvailability(inventoryAvailabilityLabel: string | undefined): JSX.Element | null {
//     if (!inventoryAvailabilityLabel || inventoryAvailabilityLabel === '') {
//         return null;
//     }

//     return (
//         <div className='msc-product__availability'>
//             <span>{inventoryAvailabilityLabel}</span>
//         </div>
//     );
// }

// /**
//  * Renders product comparison button similar to the quick view button.
//  * @param productComparisonButton - React element of the button.
//  * @param product - Current product info.
//  * @param catalogId - Current catalog.
//  * @returns React element for the specific product.
//  */
// function renderProductComparisonButton(
//     productComparisonButton: React.ReactNode,
//     product: ProductSearchResult,
//     catalogId: number
// ): JSX.Element | undefined {
//     validateCatalogId(catalogId);
//     return React.cloneElement(productComparisonButton as React.ReactElement, { product, catalogId });
// }

/**
 * Gets the product page url from the default swatch selected.
 * @param  coreContext - Context of the module using the component.
 * @param productUrl - Product page url for the product card.
 * @param  productData - Product card to be rendered.
 * @returns The product card image url.
 */
export function getProductPageUrlFromDefaultSwatch(
    coreContext: ICoreContext,
    productUrl: string,
    productData?: ProductSearchResult
): string | undefined {
    const defaultSwatch = getDefaultSwatchSelected(coreContext, productData);
    if (!defaultSwatch?.SwatchValue) {
        return productUrl;
    }

    const siteContext = coreContext as ICoreContext<IDimensionsApp>;
    const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    const queryStringEncoded = encodeURIComponent(defaultSwatch.SwatchValue);
    const queryString = `${dimensionToPreSelectInProductCard}=${queryStringEncoded}`;
    return updateProductUrl(productUrl, coreContext, queryString);
}

export const MilProductComponent: React.FC<IMilProductComponentProps> = ({
    data,
    context,
    imageSettings,
    savingsText,
    freePriceText,
    originalPriceText,
    currentPriceText,
    shouldDisplayPrice,
    ratingAriaLabel,
    // ratingCountAriaLabel,
    allowBack,
    typeName,
    id,
    telemetryContent,
    quickViewButton,
    // productComparisonButton,
    // isPriceMinMaxEnabled,
    // priceResources,
    // inventoryLabel,
    // dimensionAvailabilities,
    // swatchItemAriaLabel,
    desiredNonstockCommerceName,
    nonstockCommerceState,
    stockStatusText,
    productUom,
    surchargeLabel,
    productBehavior,
    minimumOrderQtyLabelText,
    showListView,
    availInvs,
    availableStockLabel,
    context: {
        app: {
            config: { displayStockStatus }
        }
    }
}) => {
    const product = data.product;

    let productUrl = getProductPageUrlSync(product?.Name ?? '', product?.RecordId ?? Number.MIN_VALUE, context.actionContext, undefined);
    if (allowBack) {
        productUrl = updateProductUrl(productUrl, context, 'back=true');
    }
    const productImageUrlFromSwatch = getProductImageUrlFromDefaultSwatch(context, product) ?? product?.PrimaryImageUrl;
    const productPageUrlFromSwatch = getProductPageUrlFromDefaultSwatch(context, productUrl, product) ?? productUrl;
    const [productPageUrl, setProductPageUrl] = useState<string>(productPageUrlFromSwatch);
    const [productImageUrl, setProductImageUrl] = useState<string | undefined>(productImageUrlFromSwatch);
    React.useEffect(() => {
        setProductPageUrl(productPageUrlFromSwatch);
        setProductImageUrl(productImageUrlFromSwatch);
    }, [productUrl, productPageUrlFromSwatch, productImageUrlFromSwatch]);
    const [selectedSwatchItems] = useState(new Dictionary<DimensionTypes, ISwatchItem>());
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment,@typescript-eslint/no-unsafe-member-access -- app context is generic
    // const enableStockCheck = context.app.config.enableStockCheck;

    // const siteContext = context as ICoreContext<IDimensionsApp>;
    // const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;

    // /**
    //  * Updates the product page and Image url based on swatch selected.
    //  * @param coreContext - Context of the caller.
    //  * @param swatchItem - Dimension swatch selected.
    //  */
    // const updatePageAndImageUrl = React.useCallback(
    //     (coreContext: ICoreContext, swatchItem: ISwatchItem) => {
    //         const dimensionType = swatchItem.dimensionType;
    //         selectedSwatchItems.setValue(dimensionType, swatchItem);
    //         if (StringExtensions.isNullOrWhitespace(swatchItem.value)) {
    //             return;
    //         }
    //         const queryString = `${dimensionType}=${swatchItem.value}`;
    //         let productPageUrlWithSwatch = '';
    //         if (productPageUrl.includes(dimensionType)) {
    //             const newUrl = new URL(productPageUrl, coreContext.request.apiSettings.baseUrl);
    //             newUrl.searchParams.delete(dimensionType);
    //             productPageUrlWithSwatch = updateProductUrl(newUrl.toString(), context, queryString);
    //         } else {
    //             productPageUrlWithSwatch = updateProductUrl(productPageUrl, context, queryString);
    //         }
    //         setProductPageUrl(productPageUrlWithSwatch);
    //         if (dimensionType === dimensionToPreSelectInProductCard) {
    //             const swatchProductImageUrl = ArrayExtensions.hasElements(swatchItem.productImageUrls)
    //                 ? swatchItem.productImageUrls[0]
    //                 : undefined;
    //             const newImageUrl = generateImageUrl(swatchProductImageUrl, coreContext.request.apiSettings);
    //             setProductImageUrl(newImageUrl);
    //         }
    //     },
    //     [selectedSwatchItems, context, productPageUrl]
    // );

    if (!product) {
        return null;
    }

    // /**
    //  * Checks if rendering the particular dimensions is allowed for product card.
    //  * @param dimensionType - Dimension to be displayed.
    //  * @returns Updates the state with new product page url.
    //  */
    // function shouldDisplayDimension(dimensionType: string): boolean {
    //     const dimensionsContext = context as ICoreContext<IDimensionsApp>;
    //     const dimensionsToDisplayOnProductCard = dimensionsContext.app.config.dimensionsInProductCard;
    //     return (
    //         ArrayExtensions.hasElements(dimensionsToDisplayOnProductCard) &&
    //         !dimensionsToDisplayOnProductCard.includes(DimensionTypes.none) &&
    //         dimensionsToDisplayOnProductCard.includes(dimensionType.toLocaleLowerCase() as DimensionTypes)
    //     );
    // }

    // /**
    //  * Gets the react node for product dimension as swatch.
    //  * @param  attributeValues - Attribute value property from product.
    //  * @returns The node representing markup for unit of measure component.
    //  */
    // function renderProductDimensions(attributeValues?: AttributeValue[]): JSX.Element | null {
    //     if (!attributeValues) {
    //         return null;
    //     }

    //     return (
    //         <div className='msc-product__dimensions'>
    //             {attributeValues.map((item: AttributeValue) => {
    //                 const dimensionTypeValue = item.KeyName?.toLocaleLowerCase() ?? '';
    //                 if (!shouldDisplayDimension(dimensionTypeValue)) {
    //                     return null;
    //                 }

    //                 const siteContext = context as ICoreContext<IDimensionsApp>;
    //                 const dimensionToPreSelectInProductCard = siteContext.app.config.dimensionToPreSelectInProductCard;
    //                 const dimensionType = dimensionTypeValue as DimensionTypes;
    //                 const swatches =
    //                     item.Swatches?.map<ISwatchItem>(swatchItem => {
    //                         return {
    //                             itemId: `${item.RecordId ?? ''}-${dimensionTypeValue}-${swatchItem.SwatchValue ?? ''}`,
    //                             value: swatchItem.SwatchValue ?? '',
    //                             dimensionType,
    //                             colorHexCode: swatchItem.SwatchColorHexCode,
    //                             imageUrl: swatchItem.SwatchImageUrl,
    //                             productImageUrls: swatchItem.ProductImageUrls,
    //                             isDefault: swatchItem.IsDefault,
    //                             swatchItemAriaLabel: swatchItemAriaLabel ? format(swatchItemAriaLabel, dimensionType) : '',
    //                             isDisabled:
    //                                 enableStockCheck &&
    //                                 dimensionAvailabilities?.find(
    //                                     dimensionAvailability => dimensionAvailability.value === (swatchItem.SwatchValue ?? '')
    //                                 )?.isDisabled
    //                         };
    //                     }) ?? [];
    //                 if (
    //                     dimensionType === dimensionToPreSelectInProductCard &&
    //                     ArrayExtensions.hasElements(swatches) &&
    //                     !swatches.some(swatch => swatch.isDefault)
    //                 ) {
    //                     swatches[0].isDefault = true;
    //                 }
    //                 return (
    //                     <ProductComponentSwatchComponent
    //                         context={context}
    //                         swatches={swatches}
    //                         onSelectDimension={updatePageAndImageUrl}
    //                         key={item.RecordId}
    //                     />
    //                 );
    //             })}
    //         </div>
    //     );
    // }

    function renderQuickView(quickview: React.ReactNode, item?: number): JSX.Element | undefined {
        if (quickview === null) {
            return undefined;
        }
        const selectedDimensions: ProductDimension[] = selectedSwatchItems.getValues().map<ProductDimension>(swatches => {
            return {
                DimensionTypeValue: convertDimensionTypeToProductDimensionType(swatches.dimensionType),
                DimensionValue: {
                    RecordId: 0,
                    Value: swatches.value
                }
            };
        });
        return React.cloneElement(quickview as React.ReactElement, { selectedProductId: item, selectedDimensions });
    }

    // Construct telemetry attribute to render
    const payLoad = getPayloadObject('click', telemetryContent!, '', product.RecordId.toString());

    const attribute = getTelemetryAttributes(telemetryContent!, payLoad);

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access -- -- Do not need type check for appsettings
    // const isUnitOfMeasureEnabled = context.app.config && context.app.config.unitOfMeasureDisplayType === 'buyboxAndBrowse';

    // let unitMultipliedBasePrice = product.BasePrice;
    let unitMultipliedPrice = Math.min(product.Price, product.BasePrice || 0);
    let priceUnitValue: number = 1;

    const surchargeAmount = productUom?.EcommSurchargeAmount || 0;
    const surchargeFactor = productUom?.EcommSurchargeFactor || 0;
    const isSurchargePercent = productUom?.IsEcommSurchargePercent || false;

    const calculateSurchargePerDefaultUnit = (unitPrice: number) => {
        if (surchargeAmount && surchargeFactor) {
            if (isSurchargePercent) {
                return unitPrice * (surchargeAmount / 100);
            } else {
                return surchargeAmount / surchargeFactor;
            }
        }
        return 0;
    };

    // const basePriceSurchargeAmountPerDefaultUnit = calculateSurchargePerDefaultUnit(unitMultipliedBasePrice ?? 0);
    const surchargeAmountPerDefaultUnit = calculateSurchargePerDefaultUnit(unitMultipliedPrice ?? 0);

    const priceUnit = productUom?.EcommPriceUnit || 1;
    if (priceUnit && unitMultipliedPrice) {
        unitMultipliedPrice = unitMultipliedPrice * priceUnit;
        priceUnitValue = priceUnit;
    }

    const unitMultipliedSurchargeAmount = priceUnitValue * surchargeAmountPerDefaultUnit;

    const formatAmount = (context: ICoreContext, amount: number | undefined, currencyCode?: string | undefined) => {
        if (amount === undefined) {
            return '';
        }
        return currencyCode
            ? context.cultureFormatter.formatCurrency(amount, currencyCode)
            : context.cultureFormatter.formatCurrency(amount);
    };

    const surchargeLabelToDisplay: string | null = !!unitMultipliedSurchargeAmount
        ? `${surchargeLabel || 'Surcharge incl.'} ${formatAmount(context, unitMultipliedSurchargeAmount)}`
        : null;

    let unitDescription = '';
    const uomDesc = productUom?.DefaultDescription;
    if (uomDesc) {
        unitDescription = `${priceUnitValue && priceUnitValue > 1 ? priceUnitValue : ''} ${uomDesc}`;
    } else {
        unitDescription = `${product.DefaultUnitOfMeasure || ''}`;
    }

    const productStockStatus = GetExtensionPropertyValueByKey(
        product.ExtensionProperties,
        'stock-status',
        CommercePropertyValueEnum.string
    );

    let stockStatus = productStockStatus;

    if (
        stockStatus &&
        typeof stockStatus === 'string' &&
        stockStatus.toLocaleLowerCase() === nonstockCommerceState?.toLocaleLowerCase() &&
        desiredNonstockCommerceName
    ) {
        stockStatus = desiredNonstockCommerceName;
    }

    const multipleAmount = productBehavior?.MultipleOfQuantity || 1;
    const minimumAmount = productBehavior?.MinimumQuantity || 1;
    const minimumQty = minimumAmount < multipleAmount ? multipleAmount : minimumAmount;

    return (
        <>
            {showListView ? (
                <>
                    <div className='msc-product-list' {...attribute}>
                        <a
                            href={productPageUrl}
                            onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                            aria-label={renderLabel(
                                product.Name,
                                // context.cultureFormatter.formatCurrency(product.Price),
                                context.cultureFormatter.formatCurrency(unitMultipliedPrice),
                                product.AverageRating,
                                ratingAriaLabel
                            )}
                            {...attribute}
                            className='msc-product-list__image'
                        >
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </a>
                        <div className='msc-product-list__details-container'>
                            <a
                                href={productPageUrl}
                                onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                                aria-label={renderLabel(
                                    product.Name,
                                    // context.cultureFormatter.formatCurrency(product.Price),
                                    context.cultureFormatter.formatCurrency(unitMultipliedPrice),
                                    product.AverageRating,
                                    ratingAriaLabel
                                )}
                                {...attribute}
                                className='msc-product-list__details'
                            >
                                <h5 className='msc-product-list__title'>{product.Name}</h5>
                                <div className='msc-product-list-info'>
                                    <div className='msc-product-list-info__info'>
                                        <div className='details-first-row'>
                                            <h5 className='msc-product-list-info__info__item-id'>{product.ItemId}</h5>
                                            {displayStockStatus && stockStatus && (
                                                <h5 className='msc-product-list-info__info__stock-status'>
                                                    {stockStatusText} {stockStatus}
                                                </h5>
                                            )}
                                            {shouldDisplayPrice && (
                                                <h5 className='msc-product-list-info__info__price-per'>{`Price Per ${unitDescription}`}</h5>
                                            )}
                                        </div>
                                        <div className='details-second-row'>
                                            <h5 className='msc-product-list-info__info__min-qty'>
                                                {minimumOrderQtyLabelText} {minimumQty}
                                            </h5>
                                            {availInvs && (
                                                <h5 className='msc-product-list-info__info__total-stock'>
                                                    {availableStockLabel} {availInvs?.TotalAvailableQuantity?.toLocaleString()}
                                                </h5>
                                            )}
                                        </div>
                                    </div>
                                    {shouldDisplayPrice && (
                                        <div className='msc-product-list-info__price'>
                                            {renderPrice(
                                                context,
                                                typeName,
                                                id,
                                                unitMultipliedSurchargeAmount + unitMultipliedPrice,
                                                unitMultipliedSurchargeAmount + unitMultipliedPrice,
                                                savingsText,
                                                freePriceText,
                                                originalPriceText,
                                                currentPriceText
                                            )}
                                            {surchargeLabelToDisplay && (
                                                <h5 className='msc-product-list-info__price__surcharge'>{surchargeLabelToDisplay}</h5>
                                            )}
                                        </div>
                                    )}
                                </div>
                                {/* {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)} */}
                            </a>
                            {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
                        </div>
                    </div>
                </>
            ) : (
                <>
                    <a
                        href={productPageUrl}
                        onClick={onTelemetryClick(telemetryContent!, payLoad, product.Name!)}
                        aria-label={renderLabel(
                            product.Name,
                            // context.cultureFormatter.formatCurrency(product.Price),
                            context.cultureFormatter.formatCurrency(unitMultipliedPrice),
                            product.AverageRating,
                            ratingAriaLabel
                        )}
                        className='msc-product'
                        {...attribute}
                    >
                        <div className='msc-product__image'>
                            {renderProductPlacementImage(
                                imageSettings,
                                context.request.gridSettings,
                                productImageUrl,
                                product.PrimaryImageUrl,
                                product.Name,
                                context
                            )}
                        </div>
                        <div className='msc-product__details'>
                            <h5 className='msc-product__title'>{product.Name}</h5>
                            <h5 className='msc-product__title'>{product.ItemId}</h5>
                            {displayStockStatus && stockStatus && (
                                <h4 className='msc-product__stock-status'>
                                    {stockStatusText} {stockStatus}
                                </h4>
                            )}
                            <h5 className='msc-product__min-qty'>
                                {minimumOrderQtyLabelText} {minimumQty}
                            </h5>
                            {availInvs && (
                                <h5 className='msc-product__total-stock'>
                                    {availableStockLabel} {availInvs?.TotalAvailableQuantity?.toLocaleString()}
                                </h5>
                            )}
                            {shouldDisplayPrice && (
                                <>
                                    <h5 className='msc-product__price-per'>{`Price Per ${unitDescription}`}</h5>
                                    {renderPrice(
                                        context,
                                        typeName,
                                        id,
                                        unitMultipliedSurchargeAmount + unitMultipliedPrice,
                                        unitMultipliedSurchargeAmount + unitMultipliedPrice,
                                        savingsText,
                                        freePriceText,
                                        originalPriceText,
                                        currentPriceText
                                    )}
                                    {surchargeLabelToDisplay && <h5 className='msc-product__surcharge'>{surchargeLabelToDisplay}</h5>}
                                </>
                            )}

                            {/* {isUnitOfMeasureEnabled && renderProductUnitOfMeasure(product.DefaultUnitOfMeasure)} */}
                        </div>
                    </a>
                    {quickViewButton && renderQuickView(quickViewButton, product.RecordId)}
                </>
            )}
            {/* {renderProductDimensions(product.AttributeValues)} */}
            {/* {!context.app.config.hideRating &&
                renderRating(context, typeName, id, product.AverageRating, product.TotalRatings, ratingAriaLabel, ratingCountAriaLabel)} */}
            {/* {renderProductAvailability(inventoryLabel)} */}
            {/* {productComparisonButton && renderProductComparisonButton(productComparisonButton, product, getCatalogId(context.request))} */}
        </>
    );
};

export function renderLabel(
    name?: string,
    price?: string,
    rating?: number,
    ratingAriaLabelText?: string,
    reviewCount?: number,
    ratingCountAriaLabelText?: string
): string {
    const reviewCountArialableText = getReviewAriaLabel(reviewCount, ratingCountAriaLabelText ?? '');
    return `${name ?? ''} ${price ?? ''} ${getRatingAriaLabel(rating, ratingAriaLabelText)}${
        reviewCountArialableText ? ` ${reviewCountArialableText}` : ''
    }`;
}

function getRatingAriaLabel(rating?: number, ratingAriaLabel?: string): string {
    if (rating && ratingAriaLabel) {
        const roundedRating = rating.toFixed(2);
        return format(ratingAriaLabel || '', roundedRating, '5');
    }
    return '';
}

function getReviewAriaLabel(reviewCount?: number, ratingCountAriaLabelText?: string): string {
    if (reviewCount && ratingCountAriaLabelText) {
        return format(ratingCountAriaLabelText || '', reviewCount);
    }
    return '';
}

// function renderRating(
//     coreContext: ICoreContext,
//     moduleTypeName: string,
//     moduleId: string,
//     avgRating?: number,
//     totalRatings?: number,
//     ariaLabel?: string,
//     ratingCountAriaLabel?: string
// ): JSX.Element | null {
//     if (!avgRating) {
//         return null;
//     }

//     const numberRatings = totalRatings?.toString() || undefined;
//     const ratingAriaLabelText = getRatingAriaLabel(avgRating, ariaLabel);
//     const ratingCountAriaLabelText = getReviewAriaLabel(Number(numberRatings), ratingCountAriaLabel);

//     return (
//         <RatingComponent
//             context={coreContext}
//             id={moduleId}
//             typeName={moduleTypeName}
//             avgRating={avgRating}
//             ratingCount={numberRatings}
//             readOnly
//             ariaLabel={ratingAriaLabelText}
//             ratingCountAriaLabel={ratingCountAriaLabelText}
//             data={{}}
//         />
//     );
// }

export function renderPrice(
    context: ICoreContext,
    typeName: string,
    id: string,
    basePrice?: number,
    adjustedPrice?: number,
    savingsText?: string,
    freePriceText?: string,
    originalPriceText?: string,
    currentPriceText?: string,
    isPriceMinMaxEnabled?: boolean,
    priceResources?: IPriceComponentResources
): JSX.Element | null {
    const price: ProductPrice = {
        BasePrice: basePrice,
        AdjustedPrice: adjustedPrice,
        CustomerContextualPrice: adjustedPrice
    };

    return (
        <PriceComponent
            context={context}
            id={id}
            typeName={typeName}
            data={{ price }}
            savingsText={savingsText}
            freePriceText={freePriceText}
            originalPriceText={originalPriceText}
            isPriceMinMaxEnabled={isPriceMinMaxEnabled}
            priceResources={priceResources}
        />
    );
}

export function renderProductPlacementImage(
    imageSettings?: IImageSettings,
    gridSettings?: IGridSettings,
    imageUrl?: string,
    fallbackImageUrl?: string,
    altText?: string,
    context?: ICoreContext<IGeneric<IAny>>
): JSX.Element | null {
    if (!gridSettings || !imageSettings) {
        return null;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment -- Site level config can be of any type.
    const contextConfig = context?.app.config?.placeholderImageName;
    const emptyPlaceHolderImage = contextConfig as string;
    let fallbackImageSource = fallbackImageUrl;
    if (emptyPlaceHolderImage && fallbackImageUrl) {
        fallbackImageSource = `${fallbackImageUrl},${emptyPlaceHolderImage}`;
    }

    const img: IImageData = {
        src: imageUrl,
        altText: altText ? altText : '',
        fallBackSrc: fallbackImageSource
    };

    const imageProps = {
        gridSettings,
        imageSettings
    };

    imageProps.imageSettings.cropFocalRegion = true;

    if (!imageUrl || !fallbackImageUrl) {
        return renderEmptyImage(imageSettings, context);
    }

    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            {...img}
            {...imageProps}
            loadFailureBehavior='empty'
            bypassHideOnFailure
        />
    );
}

function renderEmptyImage(imageSettings: IImageSettings | undefined, context: ICoreContext<IGeneric<IAny>> | undefined) {
    return (
        <Image
            requestContext={context?.actionContext.requestContext}
            className='ms-media-gallery__item__image'
            src='empty'
            gridSettings={context?.request.gridSettings!}
            imageSettings={imageSettings}
            loadFailureBehavior='empty'
        />
    );
}
